import { useMemo, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, request, requestBlob } from '../api/client';

interface CloudConfig {
  id: string;
  nome: string;
  displayPhoneNumber: string;
  ativo: boolean;
  templateOnly: boolean;
}

interface CloudTemplate {
  name: string;
  language: string;
  status: string;
  category: string;
  bodyText: string | null;
  variableCount: number;
}

interface ClienteItem {
  id: string;
  primeiroNome: string;
  telefone: string;
}
interface ClientePage {
  items: ClienteItem[];
  total: number;
}

interface BroadcastListItem {
  id: string;
  nome: string;
  cloudConfigNome: string | null;
  templateName: string;
  status: string;
  total: number;
  pendentes: number;
  enviados: number;
  entregues: number;
  lidos: number;
  falhados: number;
  criadoEm: string;
  concluidoEm: string | null;
}

interface ParsedRecipient {
  telefone: string;
  nome?: string;
  variaveis: string[];
}

type InputMode = 'paste' | 'csv' | 'clientes';

/**
 * Limite diário "seguro" de conversas novas por número (aquecimento). A Meta
 * começa números novos em tiers baixos (~250 conversas iniciadas/24h) e sobe
 * conforme volume + qualidade. Acima disso o excedente falha (135000). Usado
 * só pra avisar no painel — não bloqueia o envio.
 */
const WARMUP_DAILY_CAP = 250;

export function DisparosPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [nome, setNome] = useState('');
  const [cloudConfigId, setCloudConfigId] = useState('');
  const [splitDisparo, setSplitDisparo] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [inputMode, setInputMode] = useState<InputMode>('paste');
  const [pasteText, setPasteText] = useState('');
  const [csvRows, setCsvRows] = useState<ParsedRecipient[]>([]);
  const [csvFileName, setCsvFileName] = useState('');
  const [clienteSearch, setClienteSearch] = useState('');
  const [selectedClientes, setSelectedClientes] = useState<Record<string, ClienteItem>>({});
  const [detailId, setDetailId] = useState<string | null>(null);
  // Variáveis fixas: um único conjunto de valores aplicado a TODOS os contatos
  // (ignora os valores por linha/CSV/cliente enquanto ligado).
  const [fixedVars, setFixedVars] = useState(false);
  const [fixedValues, setFixedValues] = useState<string[]>([]);

  const { data: configs } = useQuery<CloudConfig[]>({
    queryKey: ['cloud-configs'],
    queryFn: () => request<CloudConfig[]>('/admin/whatsapp-cloud/configs'),
  });

  const {
    data: templates,
    isLoading: loadingTemplates,
    isError: templatesError,
    error,
  } = useQuery<CloudTemplate[]>({
    queryKey: ['cloud-templates', cloudConfigId],
    queryFn: () =>
      request<CloudTemplate[]>(`/admin/whatsapp-cloud/configs/${cloudConfigId}/templates`),
    enabled: !!cloudConfigId,
  });

  const { data: clientesData } = useQuery<ClientePage>({
    queryKey: ['clientes-disparo', clienteSearch],
    queryFn: () => {
      const qs = clienteSearch ? `?search=${encodeURIComponent(clienteSearch)}` : '';
      return request<ClientePage>(`/admin/clientes${qs}`);
    },
    enabled: inputMode === 'clientes',
  });

  const { data: broadcasts } = useQuery<BroadcastListItem[]>({
    queryKey: ['broadcasts'],
    queryFn: () => request<BroadcastListItem[]>('/admin/broadcasts'),
    refetchInterval: 10_000,
  });

  const selectedTemplate = useMemo(
    () => (templates ?? []).find((t) => t.name === templateName) ?? null,
    [templates, templateName],
  );
  const varCount = selectedTemplate?.variableCount ?? 0;
  // Com variáveis fixas ligado, a entrada de contatos só precisa do telefone —
  // os valores das variáveis vêm dos campos fixos, não de cada linha.
  const useFixed = fixedVars && varCount > 0;
  const inputVarCount = useFixed ? 0 : varCount;

  // Lista unificada de destinatários a partir do modo selecionado.
  const recipients = useMemo<ParsedRecipient[]>(() => {
    if (inputMode === 'paste') return parseLines(pasteText);
    if (inputMode === 'csv') return csvRows;
    return Object.values(selectedClientes).map((c) => ({
      telefone: c.telefone,
      nome: c.primeiroNome,
      variaveis: varCount > 0 ? padArray([c.primeiroNome], varCount) : [],
    }));
  }, [inputMode, pasteText, csvRows, selectedClientes, varCount]);

  // Aplica os valores fixos por cima de todos os contatos quando ligado.
  const effectiveRecipients = useMemo<ParsedRecipient[]>(() => {
    if (!useFixed) return recipients;
    const vals = padArray(fixedValues, varCount);
    return recipients.map((r) => ({ ...r, variaveis: vals }));
  }, [recipients, useFixed, fixedValues, varCount]);

  const validRecipients = effectiveRecipients.filter((r) => digits(r.telefone).length >= 12);
  const fixedIncompleto = useFixed && padArray(fixedValues, varCount).some((v) => !v.trim());
  const varMismatch =
    varCount > 0 && !useFixed && validRecipients.some((r) => r.variaveis.length < varCount);

  const create = useMutation({
    mutationFn: () =>
      request<{
        broadcastId: string;
        total: number;
        excluidosOptOut: number;
        estimativaConclusaoSegundos: number;
      }>('/admin/broadcasts', {
        method: 'POST',
        body: {
          nome,
          cloudConfigId,
          templateName: selectedTemplate?.name,
          templateLanguage: selectedTemplate?.language,
          templateCategory: selectedTemplate?.category,
          templateBody: selectedTemplate?.bodyText,
          recipients: validRecipients,
          // Split round-robin entre os números "só disparo" (cada um usa o
          // template dele). Só manda quando o toggle está ligado e há ≥2.
          splitConfigIds:
            splitDisparo && splitPool.length >= 2 ? splitPool.map((c) => c.id) : undefined,
        },
      }),
    onSuccess: () => {
      setNome('');
      setPasteText('');
      setCsvRows([]);
      setCsvFileName('');
      setSelectedClientes({});
      void queryClient.invalidateQueries({ queryKey: ['broadcasts'] });
    },
  });

  function handleCsv(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      setCsvRows(parseLines(text));
    };
    reader.readAsText(file);
  }

  const dispatchConfigs = (configs ?? []).filter((c) => c.ativo);
  // Pool de split = números ativos marcados como "só disparo".
  const splitPool = dispatchConfigs.filter((c) => c.templateOnly);
  const canSubmit =
    !!nome.trim() &&
    !!cloudConfigId &&
    !!selectedTemplate &&
    validRecipients.length > 0 &&
    !fixedIncompleto &&
    !create.isPending;

  return (
    <section className="space-y-6 max-w-4xl">
      <header>
        <h1 className="h1">Disparo de templates</h1>
        <p className="muted text-sm mt-1">
          Envia um template aprovado da Meta pra uma lista de contatos por um número Cloud. Use um
          número marcado como <strong>📣 Só disparo</strong> pra a Sara não responder quem interagir
          — as respostas caem no Chatwoot e no /chats pra atendimento manual.
        </p>
      </header>

      <DisparosDashboard />

      <OptOutsCard />

      <form
        className="card space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) create.mutate();
        }}
      >
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="d-nome">
              Nome da campanha
            </label>
            <input
              id="d-nome"
              className="input"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Promoção de Maio"
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="d-config">
              Número de disparo
            </label>
            <select
              id="d-config"
              className="input"
              value={cloudConfigId}
              onChange={(e) => {
                setCloudConfigId(e.target.value);
                setTemplateName('');
              }}
              required
            >
              <option value="">Selecione…</option>
              {dispatchConfigs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome} · {formatPhone(c.displayPhoneNumber)}
                  {c.templateOnly ? ' · 📣' : ''}
                </option>
              ))}
            </select>
            {splitPool.length >= 2 && (
              <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={splitDisparo}
                  onChange={(e) => setSplitDisparo(e.target.checked)}
                />
                <span>
                  Dividir entre os {splitPool.length} números “Só disparo” (round-robin) — cada
                  número usa o template dele
                </span>
              </label>
            )}
          </div>
        </div>

        {/* Template */}
        {cloudConfigId && (
          <div>
            <label className="label" htmlFor="d-template">
              Template aprovado
            </label>
            {loadingTemplates ? (
              <p className="text-sm muted">Buscando templates da Meta…</p>
            ) : templatesError ? (
              <p className="text-sm text-sara-danger">
                {error instanceof ApiError ? error.message : 'Erro ao buscar templates.'}
              </p>
            ) : (templates ?? []).length === 0 ? (
              <p className="text-sm muted">
                Nenhum template aprovado nessa conta. Crie e aprove um no Gerenciador da Meta
                primeiro.
              </p>
            ) : (
              <select
                id="d-template"
                className="input"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                required
              >
                <option value="">Selecione…</option>
                {(templates ?? []).map((t) => (
                  <option key={`${t.name}:${t.language}`} value={t.name}>
                    {t.name} ({t.language})
                    {t.variableCount > 0 ? ` · ${t.variableCount} variável(is)` : ''}
                  </option>
                ))}
              </select>
            )}
            {selectedTemplate?.bodyText && (
              <div className="mt-2 rounded-lg border border-sara-border bg-sara-bg p-3 text-sm whitespace-pre-wrap">
                {selectedTemplate.bodyText}
              </div>
            )}

            {/* Variáveis fixas pra todos os contatos */}
            {varCount > 0 && (
              <div className="mt-3 rounded-lg border border-sara-border bg-sara-bg p-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fixedVars}
                    onChange={(e) => setFixedVars(e.target.checked)}
                  />
                  <span className="font-medium text-sara-text">
                    Usar variáveis fixas pra todos os contatos
                  </span>
                </label>
                {fixedVars ? (
                  <div className="mt-3 space-y-2">
                    {Array.from({ length: varCount }).map((_, i) => (
                      <div key={i}>
                        <label className="label" htmlFor={`d-fixed-${i}`}>
                          Valor de <code className="text-sara-accent">{`{{${i + 1}}}`}</code>
                        </label>
                        <input
                          id={`d-fixed-${i}`}
                          className="input"
                          value={fixedValues[i] ?? ''}
                          onChange={(e) =>
                            setFixedValues((prev) => {
                              const next = [...prev];
                              while (next.length < varCount) next.push('');
                              next[i] = e.target.value;
                              return next;
                            })
                          }
                          placeholder={`Texto fixo da variável ${i + 1}`}
                        />
                      </div>
                    ))}
                    <p className="text-xs text-sara-text-subtle">
                      Esses valores vão pra <strong>todos</strong> os contatos da lista. Os valores
                      por linha/CSV/cliente são ignorados enquanto isso estiver ligado — você só
                      precisa colar os telefones.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-sara-text-subtle mt-1">
                    Ligado, todos os contatos recebem os mesmos valores nas {varCount} variável(is).
                    Desligado, cada contato traz os próprios valores (após o telefone na lista/CSV).
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Contatos */}
        <div>
          <span className="label">Contatos</span>
          <div className="flex gap-2 mb-3" role="tablist">
            {(
              [
                ['paste', '📋 Colar lista'],
                ['csv', '📄 CSV'],
                ['clientes', '👥 Clientes do banco'],
              ] as [InputMode, string][]
            ).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                role="tab"
                aria-selected={inputMode === mode}
                className={inputMode === mode ? 'btn-primary text-xs' : 'btn-ghost text-xs'}
                onClick={() => setInputMode(mode)}
              >
                {label}
              </button>
            ))}
          </div>

          {inputMode === 'paste' && (
            <div>
              <textarea
                className="input font-mono text-sm min-h-[140px]"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={
                  inputVarCount > 0
                    ? `Um por linha: telefone, valor1${inputVarCount > 1 ? ', valor2' : ''}…\n5511999999999, Maria\n5521988887777, João`
                    : 'Um telefone por linha:\n5511999999999\n5521988887777'
                }
              />
              <p className="text-xs text-sara-text-subtle mt-1">
                {inputVarCount > 0
                  ? `Esse template tem ${inputVarCount} variável(is). Coloque os valores após o telefone, separados por vírgula, na ordem {{1}}, {{2}}…`
                  : useFixed
                    ? 'Variáveis fixas ligadas — só os telefones, um por linha.'
                    : 'Esse template não tem variáveis — só os telefones.'}
              </p>
            </div>
          )}

          {inputMode === 'csv' && (
            <div>
              <input type="file" accept=".csv,text/csv" onChange={handleCsv} className="text-sm" />
              {csvFileName && (
                <p className="text-xs muted mt-1">
                  {csvFileName} · {csvRows.length} linha(s) lida(s)
                </p>
              )}
              <p className="text-xs text-sara-text-subtle mt-1">
                1ª coluna = telefone; colunas seguintes = variáveis na ordem. Linha de cabeçalho é
                ignorada automaticamente.
              </p>
            </div>
          )}

          {inputMode === 'clientes' && (
            <div className="space-y-2">
              <input
                className="input text-sm"
                value={clienteSearch}
                onChange={(e) => setClienteSearch(e.target.value)}
                placeholder="Buscar nome ou telefone…"
              />
              <div className="max-h-48 overflow-auto rounded-lg border border-sara-border divide-y divide-sara-border">
                {(clientesData?.items ?? []).map((c) => {
                  const checked = !!selectedClientes[c.id];
                  return (
                    <label
                      key={c.id}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-sara-surface-hover"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelectedClientes((prev) => {
                            const next = { ...prev };
                            if (next[c.id]) delete next[c.id];
                            else next[c.id] = c;
                            return next;
                          })
                        }
                      />
                      <span className="flex-1">{c.primeiroNome || '(sem nome)'}</span>
                      <span className="muted text-xs">{formatPhone(c.telefone)}</span>
                    </label>
                  );
                })}
                {(clientesData?.items ?? []).length === 0 && (
                  <p className="text-sm muted px-3 py-2">Nenhum cliente encontrado.</p>
                )}
              </div>
              {varCount > 0 && (
                <p className="text-xs text-sara-text-subtle">
                  {useFixed
                    ? 'Variáveis fixas ligadas — todos recebem os mesmos valores definidos acima.'
                    : `${'{{1}}'} será preenchido com o primeiro nome do cliente.`}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Preview */}
        {validRecipients.length > 0 && (
          <div className="rounded-lg border border-sara-border bg-sara-bg p-3">
            <p className="text-sm font-medium text-sara-text">
              {validRecipients.length} destinatário(s) válido(s)
              {recipients.length !== validRecipients.length &&
                ` · ${recipients.length - validRecipients.length} descartado(s) (telefone inválido)`}
            </p>
            {varMismatch && (
              <p className="text-xs text-sara-warning mt-1">
                ⚠ Alguns contatos têm menos variáveis que o template pede ({varCount}). Eles serão
                enviados com os campos faltantes em branco.
              </p>
            )}
            {validRecipients.length > WARMUP_DAILY_CAP && (
              <div className="mt-2 rounded-lg border border-sara-warning/40 bg-sara-warning/10 p-2.5 text-xs text-sara-warning">
                <p className="font-medium">
                  ⚠ {validRecipients.length} contatos — acima do limite diário seguro (~
                  {WARMUP_DAILY_CAP}).
                </p>
                <p className="mt-1 text-sara-text-muted">
                  A Meta limita quantas <strong>conversas novas</strong> um número pode iniciar por
                  dia (números novos começam baixo e o limite sobe conforme o aquecimento). Acima
                  disso, o excedente falha com erro <code>135000</code> e pode derrubar a qualidade
                  do número. Recomendado: enviar em lotes de até ~{WARMUP_DAILY_CAP}/dia e deixar o
                  resto pros próximos dias.
                </p>
              </div>
            )}
            <ul className="mt-2 text-xs font-mono space-y-0.5 max-h-32 overflow-auto">
              {validRecipients.slice(0, 30).map((r, i) => (
                <li key={`${r.telefone}-${i}`} className="muted">
                  {formatPhone(r.telefone)}
                  {r.variaveis.length > 0 ? ` → ${r.variaveis.join(' | ')}` : ''}
                </li>
              ))}
              {validRecipients.length > 30 && (
                <li className="text-sara-text-subtle">+ {validRecipients.length - 30} outros…</li>
              )}
            </ul>
          </div>
        )}

        {create.isError && (
          <p role="alert" className="text-sara-danger text-sm">
            {create.error instanceof ApiError ? create.error.message : 'Erro ao disparar.'}
          </p>
        )}
        {create.isSuccess && (
          <p role="status" className="text-sara-success text-sm">
            ✓ Disparo criado! {create.data.total} contato(s) na fila (~
            {create.data.estimativaConclusaoSegundos}s pra concluir).
            {create.data.excluidosOptOut > 0 && (
              <span className="block text-sara-text-muted">
                🚫 {create.data.excluidosOptOut} contato(s) pulado(s) por opt-out (clicaram em "não
                receber mais").
              </span>
            )}
          </p>
        )}

        <div className="flex justify-end pt-2 border-t border-sara-border">
          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit}
            aria-busy={create.isPending}
          >
            {create.isPending
              ? 'Disparando…'
              : `📣 Disparar pra ${validRecipients.length} contato(s)`}
          </button>
        </div>
      </form>

      {/* Histórico */}
      <div className="space-y-3">
        <h2 className="h2">Campanhas</h2>
        {!broadcasts || broadcasts.length === 0 ? (
          <div className="card muted text-sm">Nenhum disparo ainda.</div>
        ) : (
          broadcasts.map((b) => (
            <article key={b.id} className="card space-y-2">
              <header className="flex items-center justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-sara-text">{b.nome}</h3>
                  <p className="text-xs muted">
                    {b.templateName} · {b.cloudConfigNome ?? '?'} ·{' '}
                    {new Date(b.criadoEm).toLocaleString('pt-BR')}
                  </p>
                </div>
                <StatusBadge status={b.status} />
              </header>
              <div className="flex flex-wrap gap-2 text-xs">
                <Stat label="Total" value={b.total} />
                <Stat label="Pendentes" value={b.pendentes} />
                <Stat label="Enviados" value={b.enviados} cls="text-sara-text" />
                <Stat label="Entregues" value={b.entregues} cls="text-sara-success" />
                <Stat label="Lidos" value={b.lidos} cls="text-sara-accent" />
                <Stat label="Falhados" value={b.falhados} cls="text-sara-danger" />
              </div>
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => setDetailId(detailId === b.id ? null : b.id)}
              >
                {detailId === b.id ? 'Ocultar destinatários' : 'Ver destinatários'}
              </button>
              {detailId === b.id && <BroadcastDetail id={b.id} />}
            </article>
          ))
        )}
      </div>
    </section>
  );
}

function BroadcastDetail({ id }: { id: string }): JSX.Element {
  interface Detail {
    recipients: Array<{
      id: string;
      telefone: string;
      nome: string | null;
      status: string;
      erro: string | null;
    }>;
  }
  const { data, isLoading } = useQuery<Detail>({
    queryKey: ['broadcast-detail', id],
    queryFn: () => request<Detail>(`/admin/broadcasts/${id}`),
    refetchInterval: 10_000,
  });
  if (isLoading) return <p className="text-xs muted">Carregando…</p>;
  return (
    <div className="max-h-64 overflow-auto rounded-lg border border-sara-border divide-y divide-sara-border text-xs">
      {(data?.recipients ?? []).map((r) => (
        <div key={r.id} className="flex items-center gap-2 px-3 py-1.5">
          <span className="font-mono flex-1">{formatPhone(r.telefone)}</span>
          {r.nome && <span className="muted">{r.nome}</span>}
          <StatusBadge status={r.status} small />
          {r.erro && (
            <span className="text-sara-danger truncate max-w-[200px]" title={r.erro}>
              {r.erro}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status, small }: { status: string; small?: boolean }): JSX.Element {
  const map: Record<string, string> = {
    running: 'bg-sara-accent/15 text-sara-accent',
    done: 'bg-sara-success/15 text-sara-success',
    failed: 'bg-sara-danger/15 text-sara-danger',
    pendente: 'bg-sara-surface-hover text-sara-text-muted',
    enviado: 'bg-sara-surface-hover text-sara-text',
    entregue: 'bg-sara-success/15 text-sara-success',
    lido: 'bg-sara-accent/15 text-sara-accent',
    falhou: 'bg-sara-danger/15 text-sara-danger',
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 ${small ? 'text-[10px]' : 'text-xs'} ${map[status] ?? 'bg-sara-surface-hover'}`}
    >
      {status}
    </span>
  );
}

function Stat({ label, value, cls }: { label: string; value: number; cls?: string }): JSX.Element {
  return (
    <span className="rounded bg-sara-surface-hover px-2 py-0.5">
      <span className="muted">{label}:</span> <span className={cls}>{value}</span>
    </span>
  );
}

/** Quebra texto colado/CSV em destinatários: 1ª coluna = telefone, resto = variáveis. */
function parseLines(text: string): ParsedRecipient[] {
  const out: ParsedRecipient[] = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const cols = line.split(/[,;\t]/).map((c) => c.trim());
    const telefone = digits(cols[0] ?? '');
    if (telefone.length < 10) continue; // pula cabeçalho / linha sem telefone
    out.push({ telefone, variaveis: cols.slice(1).filter((c) => c.length > 0) });
  }
  return out;
}

function padArray(arr: string[], len: number): string[] {
  const copy = [...arr];
  while (copy.length < len) copy.push('');
  return copy.slice(0, len);
}

function digits(raw: string): string {
  return raw.replace(/\D/g, '');
}

function formatPhone(raw: string): string {
  const d = digits(raw);
  if (d.length === 13 && d.startsWith('55')) {
    return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  }
  if (d.length === 12 && d.startsWith('55')) {
    return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
  }
  return `+${d}`;
}

// ─────────────────────────── Opt-outs (não receber mais) ─────────────────────

interface OptOutItem {
  nome: string;
  telefone: string;
  email: string | null;
  optOutEm: string | null;
}

/**
 * Lista quem clicou em "não receber mais mensagens". Esses contatos já são
 * excluídos automaticamente de todo disparo — o card serve pra conferir/baixar.
 */
function OptOutsCard(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  const { data, isLoading } = useQuery<OptOutItem[]>({
    queryKey: ['broadcast-opt-outs'],
    queryFn: () => request<OptOutItem[]>('/admin/broadcasts/opt-outs'),
    refetchInterval: 30_000,
  });

  async function exportCsv(): Promise<void> {
    setExporting(true);
    setExportErr(null);
    try {
      const blob = await requestBlob('/admin/broadcasts/opt-outs/export');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'opt-outs-sara.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportErr(e instanceof ApiError ? e.message : 'Falha ao exportar.');
    } finally {
      setExporting(false);
    }
  }

  const total = data?.length ?? 0;

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-semibold text-sara-text">🚫 Opt-outs (não receber mais)</h2>
          <p className="muted text-xs mt-0.5">
            {isLoading
              ? 'Carregando…'
              : `${total} contato(s) que clicaram em "não receber mais". São excluídos automaticamente de todo disparo.`}
          </p>
        </div>
        <div className="flex gap-2">
          {total > 0 && (
            <button type="button" className="btn-ghost text-xs" onClick={() => setOpen((v) => !v)}>
              {open ? 'Ocultar lista' : 'Ver lista'}
            </button>
          )}
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => void exportCsv()}
            disabled={exporting || total === 0}
            aria-busy={exporting}
          >
            {exporting ? 'Baixando…' : '⬇ Baixar CSV'}
          </button>
        </div>
      </div>

      {exportErr && (
        <p role="alert" className="text-sara-danger text-xs">
          {exportErr}
        </p>
      )}

      {open && total > 0 && (
        <div className="max-h-64 overflow-auto rounded-lg border border-sara-border divide-y divide-sara-border text-xs">
          {(data ?? []).map((o, i) => (
            <div key={`${o.telefone}-${i}`} className="flex items-center gap-2 px-3 py-1.5">
              <span className="font-mono flex-1">{formatPhone(o.telefone)}</span>
              {o.nome && <span className="muted truncate max-w-[140px]">{o.nome}</span>}
              {o.optOutEm && (
                <span className="text-sara-text-subtle">
                  {new Date(o.optOutEm).toLocaleDateString('pt-BR')}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Dashboard de disparos ───────────────────────────

type Period = 'hoje' | '7d' | '30d';

interface DashboardData {
  period: Period;
  campanhas: number;
  enviadas: number;
  entregues: number;
  lidas: number;
  falhadas: number;
  pendentes: number;
  taxaEntrega: number;
  tiragensPagasPeriodo: number;
  tiragensPagasPorDisparo: number;
  custoEstimadoCentavos: number;
  custoPorCategoria: Array<{ categoria: string; mensagens: number; custoCentavos: number }>;
  porDia: Array<{
    dia: string;
    enviadas: number;
    entregues: number;
    falhadas: number;
    custoCentavos: number;
  }>;
  porNumero: Array<{
    cloudConfigId: string;
    nome: string | null;
    enviadas: number;
    entregues: number;
    custoCentavos: number;
  }>;
  numeros: Array<{
    cloudConfigId: string;
    nome: string | null;
    displayPhoneNumber: string | null;
    usadoHoje: number;
    limite: number | null;
    tier: string | null;
  }>;
  rates: { marketingCentavos: number; utilityCentavos: number; authCentavos: number };
}

const PERIOD_LABELS: Record<Period, string> = { hoje: 'Hoje', '7d': '7 dias', '30d': '30 dias' };

function brl(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function DisparosDashboard(): JSX.Element {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<Period>('7d');
  const [editRates, setEditRates] = useState(false);

  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['broadcast-dashboard', period],
    queryFn: () => request<DashboardData>(`/admin/broadcasts/dashboard?period=${period}`),
    refetchInterval: 30_000,
  });

  const maxDia = useMemo(() => Math.max(1, ...(data?.porDia ?? []).map((d) => d.enviadas)), [data]);

  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-sara-text">📊 Resumo de disparos</h2>
        <div className="flex gap-1" role="tablist" aria-label="Período">
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={period === p}
              onClick={() => setPeriod(p)}
              className={`rounded-full px-3 py-1 text-xs ${
                period === p
                  ? 'bg-sara-accent text-sara-accent-fg'
                  : 'bg-sara-surface-hover text-sara-text-muted'
              }`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      {isLoading || !data ? (
        <p className="muted text-sm">Carregando…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <MetricCard label="Campanhas" value={String(data.campanhas)} />
            <MetricCard label="Enviadas" value={String(data.enviadas)} />
            <MetricCard
              label="Entregues"
              value={String(data.entregues)}
              sub={`${Math.round(data.taxaEntrega * 100)}% de entrega`}
              cls="text-sara-success"
            />
            <MetricCard
              label="Falhadas"
              value={String(data.falhadas)}
              cls={data.falhadas > 0 ? 'text-sara-danger' : undefined}
            />
            <MetricCard
              label="Custo estimado"
              value={brl(data.custoEstimadoCentavos)}
              sub="entregues × tarifa"
              cls="text-sara-accent"
            />
          </div>

          <div className="mt-3 rounded-lg border border-sara-primary/40 bg-sara-primary/5 p-3 flex items-baseline justify-between">
            <div>
              <span className="text-sm font-semibold text-sara-text">
                Tiragens pagas geradas por disparo
              </span>
              <p className="text-xs text-sara-text-muted">
                Tiragens pagas (R$11,77) de quem recebeu disparo (manual + reativação) em até 30
                dias antes da compra. Cruzado por telefone.
              </p>
            </div>
            <span className="text-xl font-bold text-sara-primary whitespace-nowrap">
              {data.tiragensPagasPorDisparo}
              <span className="text-sm font-normal text-sara-text-muted">
                {' '}
                / {data.tiragensPagasPeriodo} pagas
              </span>
            </span>
          </div>

          {/* Disponível hoje por número (limite real da Meta) */}
          {data.numeros.length > 0 && (
            <div className="space-y-1">
              <p className="label">Disponível hoje (limite da Meta)</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {data.numeros.map((n) => {
                  const pct =
                    n.limite && n.limite > 0
                      ? Math.min(100, Math.round((n.usadoHoje / n.limite) * 100))
                      : 0;
                  return (
                    <div
                      key={n.cloudConfigId}
                      className="rounded bg-sara-surface-hover p-2 text-sm"
                    >
                      <div className="flex justify-between gap-2">
                        <span className="truncate">
                          {n.nome ?? formatPhone(n.displayPhoneNumber ?? '')}
                        </span>
                        <span className="muted">
                          {n.usadoHoje}
                          {n.limite ? ` / ${n.limite.toLocaleString('pt-BR')}` : ' enviadas'}
                        </span>
                      </div>
                      {n.limite ? (
                        <div className="mt-1 h-1.5 rounded bg-sara-bg overflow-hidden">
                          <div
                            className={`h-full ${pct >= 90 ? 'bg-sara-danger' : 'bg-sara-accent'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      ) : (
                        <p className="muted text-[11px] mt-0.5">
                          {n.tier === 'TIER_UNLIMITED'
                            ? 'limite ilimitado'
                            : 'limite não informado pela Meta'}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Por dia */}
          {data.porDia.length > 0 && (
            <div className="space-y-1">
              <p className="label">Por dia</p>
              <div className="space-y-1">
                {data.porDia.map((d) => (
                  <div key={d.dia} className="flex items-center gap-2 text-xs">
                    <span className="muted w-14 shrink-0">{d.dia.slice(5)}</span>
                    <div className="flex-1 h-3 rounded bg-sara-surface-hover overflow-hidden">
                      <div
                        className="h-full bg-sara-accent"
                        style={{ width: `${Math.round((d.enviadas / maxDia) * 100)}%` }}
                      />
                    </div>
                    <span className="w-10 text-right">{d.enviadas}</span>
                    <span className="w-20 text-right muted">{brl(d.custoCentavos)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Custo por categoria + por número */}
          <div className="grid sm:grid-cols-2 gap-4">
            {data.custoPorCategoria.length > 0 && (
              <div className="space-y-1">
                <p className="label">Custo por categoria</p>
                {data.custoPorCategoria.map((c) => (
                  <div key={c.categoria} className="flex justify-between text-sm">
                    <span>
                      {c.categoria} <span className="muted">({c.mensagens})</span>
                    </span>
                    <span className="text-sara-accent">{brl(c.custoCentavos)}</span>
                  </div>
                ))}
              </div>
            )}
            {data.porNumero.length > 0 && (
              <div className="space-y-1">
                <p className="label">Custo por número</p>
                {data.porNumero.map((n) => (
                  <div key={n.cloudConfigId} className="flex justify-between text-sm">
                    <span className="truncate">
                      {n.nome ?? n.cloudConfigId} <span className="muted">({n.entregues})</span>
                    </span>
                    <span className="text-sara-accent">{brl(n.custoCentavos)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tarifas editáveis */}
          <div className="border-t border-sara-border pt-3">
            <button
              type="button"
              className="text-xs text-sara-accent"
              onClick={() => setEditRates((v) => !v)}
            >
              {editRates ? '▲ Fechar tarifas' : '⚙️ Ajustar tarifas (R$ por mensagem entregue)'}
            </button>
            {editRates && (
              <RatesEditor
                rates={data.rates}
                onSaved={() =>
                  void queryClient.invalidateQueries({ queryKey: ['broadcast-dashboard'] })
                }
              />
            )}
            <p className="muted text-[11px] mt-1">
              Custo é estimado: a Meta não devolve o valor em R$, só a categoria. Ajuste as tarifas
              pros valores reais que você paga.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function MetricCard({
  label,
  value,
  sub,
  cls,
}: {
  label: string;
  value: string;
  sub?: string;
  cls?: string;
}): JSX.Element {
  return (
    <div className="rounded bg-sara-surface-hover p-3">
      <p className="muted text-[11px]">{label}</p>
      <p className={`text-lg font-semibold ${cls ?? 'text-sara-text'}`}>{value}</p>
      {sub && <p className="muted text-[10px]">{sub}</p>}
    </div>
  );
}

function RatesEditor({
  rates,
  onSaved,
}: {
  rates: { marketingCentavos: number; utilityCentavos: number; authCentavos: number };
  onSaved: () => void;
}): JSX.Element {
  const [marketing, setMarketing] = useState((rates.marketingCentavos / 100).toFixed(2));
  const [utility, setUtility] = useState((rates.utilityCentavos / 100).toFixed(2));
  const [auth, setAuth] = useState((rates.authCentavos / 100).toFixed(2));

  const save = useMutation({
    mutationFn: () =>
      request('/admin/broadcasts/cost-config', {
        method: 'PUT',
        body: {
          marketingCentavos: Math.round(parseFloat(marketing.replace(',', '.')) * 100) || 0,
          utilityCentavos: Math.round(parseFloat(utility.replace(',', '.')) * 100) || 0,
          authCentavos: Math.round(parseFloat(auth.replace(',', '.')) * 100) || 0,
        },
      }),
    onSuccess: onSaved,
  });

  return (
    <div className="mt-2 grid grid-cols-3 gap-2 items-end">
      <RateInput label="Marketing" value={marketing} onChange={setMarketing} />
      <RateInput label="Utility" value={utility} onChange={setUtility} />
      <RateInput label="Autenticação" value={auth} onChange={setAuth} />
      <button
        type="button"
        className="btn-primary col-span-3 text-sm"
        onClick={() => save.mutate()}
        disabled={save.isPending}
      >
        {save.isPending ? 'Salvando…' : 'Salvar tarifas'}
      </button>
    </div>
  );
}

function RateInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div>
      <label className="muted text-[11px]">{label} (R$)</label>
      <input
        className="input"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
